import type { ColorCustomizationDto } from '@backend-models/general-settings/color-customization-dto'

export type CustomColors = ColorCustomizationDto

/**
 * Apply custom colors to CSS variables
 */
export function applyCustomColors(colors: CustomColors) {
	const root = document.documentElement

	if (colors.primaryColor) {
		root.style.setProperty('--theme-primary-color', colors.primaryColor)
		root.style.setProperty('--theme-primary-background', colors.primaryColor)
	}

	if (colors.primaryHoverColor) {
		root.style.setProperty('--custom-primary-hover-color', colors.primaryHoverColor)
		root.style.setProperty('--theme-primary-hover', colors.primaryHoverColor)
	}

	if (colors.secondaryColor) {
		root.style.setProperty('--custom-secondary-color', colors.secondaryColor)
	}

	if (colors.backgroundColor) {
		root.style.setProperty('--custom-background-color', colors.backgroundColor)
		root.style.setProperty('--theme-background', colors.backgroundColor)
	}

	if (colors.surfaceColor) {
		root.style.setProperty('--custom-surface-color', colors.surfaceColor)
		root.style.setProperty('--theme-layer-background', colors.surfaceColor)
	}

	if (colors.textSecondaryColor) {
		root.style.setProperty('--custom-text-secondary-color', colors.textSecondaryColor)
	}

	if (colors.consortiumNameColor) {
		root.style.setProperty('--custom-consortium-name-color', colors.consortiumNameColor)
	}

	if (colors.headerIconsColor) {
		root.style.setProperty('--custom-header-icons-color', colors.headerIconsColor)
		if (colors.headerIconsColor === '#666968') {
			// preserve our default hover when our default color is used
			root.style.setProperty('--custom-header-icons-hover-color', 'var(--theme-primary-color)')
		} else {
			root.style.setProperty('--custom-header-icons-hover-color', darkenColor(colors.headerIconsColor, 10))
		}
	}
}

/**
 * Reset to default colors and scaling
 */
export function resetCustomization() {
	const root = document.documentElement

	// Remove custom color variables
	root.style.removeProperty('--custom-primary-color')
	root.style.removeProperty('--custom-primary-hover-color')
	root.style.removeProperty('--custom-secondary-color')
	root.style.removeProperty('--custom-background-color')
	root.style.removeProperty('--custom-surface-color')
	root.style.removeProperty('--custom-text-secondary-color')
	root.style.removeProperty('--custom-consortium-name-color')
	root.style.removeProperty('--custom-header-icons-color')
	root.style.removeProperty('--custom-header-icons-hover-color')

	// Reset theme variables to defaults
	root.style.removeProperty('--theme-primary-background')
	root.style.removeProperty('--theme-primary-hover')
	root.style.removeProperty('--theme-background')
	root.style.removeProperty('--theme-layer-background')
}

/**
 * Darken a hex color by a percentage
 */
export function darkenColor(hex: string, percent: number): string {
	// Convert hex to RGB
	const r = parseInt(hex.slice(1, 3), 16)
	const g = parseInt(hex.slice(3, 5), 16)
	const b = parseInt(hex.slice(5, 7), 16)

	// Darken
	const factor = 1 - percent / 100
	const newR = Math.round(r * factor)
	const newG = Math.round(g * factor)
	const newB = Math.round(b * factor)

	// Convert back to hex
	return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}

/**
 * Lighten a hex color by a percentage
 */
export function lightenColor(hex: string, percent: number): string {
	// Convert hex to RGB
	const r = parseInt(hex.slice(1, 3), 16)
	const g = parseInt(hex.slice(3, 5), 16)
	const b = parseInt(hex.slice(5, 7), 16)

	// Lighten
	const factor = percent / 100
	const newR = Math.round(r + (255 - r) * factor)
	const newG = Math.round(g + (255 - g) * factor)
	const newB = Math.round(b + (255 - b) * factor)

	// Convert back to hex
	return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}
